import { ErrorBoundary } from "@atlas/composite";
import { useTranslation } from "@atlas/sdk";
import type { IFederatedRemoteConfig } from "@shared/federation/types";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DynamicMicroFrontend } from "./DynamicMicroFrontend";

type KeepAliveEntry = {
  cacheKey: string;
  route: IFederatedRemoteConfig;
};

type MicroFrontendKeepAliveApi = {
  activate: (cacheKey: string, route: IFederatedRemoteConfig) => void;
  deactivate: (cacheKey: string) => void;
  activeKey: string | null;
  entries: KeepAliveEntry[];
};

const MicroFrontendKeepAliveContext =
  createContext<MicroFrontendKeepAliveApi | null>(null);

export function MicroFrontendKeepAliveProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [entries, setEntries] = useState<KeepAliveEntry[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const activeKeyRef = useRef<string | null>(null);

  const activate = useCallback(
    (cacheKey: string, route: IFederatedRemoteConfig) => {
      activeKeyRef.current = cacheKey;
      setActiveKey(cacheKey);
      setEntries((currentEntries) => {
        if (currentEntries.some((entry) => entry.cacheKey === cacheKey)) {
          return currentEntries;
        }
        return [...currentEntries, { cacheKey, route }];
      });
    },
    [],
  );

  const deactivate = useCallback((cacheKey: string) => {
    if (activeKeyRef.current !== cacheKey) {
      return;
    }
    activeKeyRef.current = null;
    setActiveKey(null);
  }, []);

  const value = useMemo<MicroFrontendKeepAliveApi>(
    () => ({
      activate,
      deactivate,
      activeKey,
      entries,
    }),
    [activate, deactivate, activeKey, entries],
  );

  return (
    <MicroFrontendKeepAliveContext.Provider value={value}>
      {children}
    </MicroFrontendKeepAliveContext.Provider>
  );
}

function useMicroFrontendKeepAlive() {
  const context = useContext(MicroFrontendKeepAliveContext);
  if (!context) {
    throw new Error(
      "MicroFrontend keep-alive components must be used within their provider",
    );
  }
  return context;
}

export function MicroFrontendKeepAliveHost() {
  const { t } = useTranslation();
  const { activeKey, entries } = useMicroFrontendKeepAlive();

  return (
    <div
      className={`flex min-h-0 min-w-0 flex-1 flex-col ${
        activeKey ? "" : "hidden"
      }`}>
      {entries.map((entry) => (
        <div
          key={entry.cacheKey}
          className={`flex min-h-0 min-w-0 flex-1 flex-col ${
            entry.cacheKey === activeKey ? "" : "hidden"
          }`}>
          <ErrorBoundary
            title={t("hub.mfe.load_failed", {
              label: entry.route.label,
              defaultValue: "Failed to load {{label}}",
            })}>
            <DynamicMicroFrontend route={entry.route} />
          </ErrorBoundary>
        </div>
      ))}
    </div>
  );
}

export function MicroFrontendRoutesShell({
  children,
}: {
  children: ReactNode;
}) {
  const { activeKey } = useMicroFrontendKeepAlive();

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col ${
        activeKey ? "pointer-events-none absolute inset-0 z-20" : ""
      }`}>
      {children}
    </div>
  );
}

export function MicroFrontendRouteSlot({
  cacheKey,
  route,
}: {
  cacheKey: string;
  route: IFederatedRemoteConfig;
}) {
  const { activate, deactivate } = useMicroFrontendKeepAlive();
  const routeRef = useRef(route);
  routeRef.current = route;

  useEffect(() => {
    activate(cacheKey, routeRef.current);
    return () => deactivate(cacheKey);
  }, [activate, deactivate, cacheKey]);

  return null;
}

export function mfeCacheKey(routeId: string) {
  return `mfe:${routeId}`;
}
